import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box, Typography, Button, Alert, Chip, CircularProgress, Divider,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
// The app's real notification system (see AiSettings.jsx's own toast
// import for the full writeup) — generate-menu errors were a plain inline
// Alert instead (Rahim, 2026-09-04 audit). The success Alert below stays —
// it's not a transient notification, it's real page content (lists the
// actually-generated items underneath it).
import { toast } from "react-hot-toast";
import { getAuthToken } from "../../../../../utils/getAuthToken";
import { SERVER_DOMAIN } from "../constants";
import { fetchCatalogs } from "../../../../../redux/Slice/Catalog/catalogSlice";

function describeAction(action) {
  if (!action) return null;
  if (action.type === "list_message") {
    const n = action.config?.options?.length || 0;
    return `Sends a list with ${n} option${n === 1 ? "" : "s"}`;
  }
  if (action.type === "button_message") {
    const n = (action.config?.buttons || "").split(",").filter(Boolean).length;
    return `Sends ${n} button${n === 1 ? "" : "s"}`;
  }
  if (action.type === "catalog_send") {
    const n = action.config?.products?.length || 0;
    const name = action.config?.catalogName || "catalog";
    return `Searches "${name}" (${n} product${n === 1 ? "" : "s"}) and sends what matches`;
  }
  const LABELS = {
    assignment: "Assigns the conversation",
    api_integration: "Calls an API",
    close_conversation: "Closes the conversation",
    image_message: "Sends an image found in the knowledge sources",
  };
  return LABELS[action.type] || action.type;
}

export default function GenerateMenuPanel({ form, setForm, workspaceId }) {
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(null); // last-applied intents, for the summary
  const [usage, setUsage] = useState(null);
  // Grounds catalog_send actions in real catalogs when generating — fetched here too
  // (not just by the catalog_send ActionConfigPanel) so generation works even if the
  // owner enabled "Enable catalog message" but hasn't opened that section yet.
  const dispatch = useDispatch();
  const { catalogs, status: catalogsStatus } = useSelector((state) => state.catalog);
  const catalogSendEnabled = (form.enabledActions || []).includes("catalog_send");

  useEffect(() => {
    if (!catalogSendEnabled) return;
    if (catalogsStatus === "loading" || catalogsStatus === "succeeded") return;
    if (catalogs && catalogs.length > 0) return;
    dispatch(fetchCatalogs());
  }, [catalogSendEnabled, catalogsStatus, catalogs, dispatch]);

  const hasEnoughInput = ["context", "flow", "businessRules"].some(
    (k) => form.instructions?.[k] && form.instructions[k].trim()
  );

  const generate = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents/generate-intents`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          instructions: form.instructions,
          enabledActions: form.enabledActions,
          actionConfigs: form.actionConfigs,
          catalogs: catalogSendEnabled ? catalogs : undefined,
          // Gates whether generated intents may use a list_message/button_message action —
          // same Message Format > Allowed formats setting that governs live AI replies.
          responsePreferences: form.responsePreferences,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.message || "Failed to generate menu"); return; }

      const generatedIntents = data.intents || [];
      if (!generatedIntents.length) { toast.error("The AI didn't generate any menu items — try adding more detail to Context or Conversation Flow."); return; }

      const existingNames = new Set((form.intents || []).map((i) => i.name));
      const withReplaceFlag = generatedIntents.map((i) => ({ ...i, isReplace: existingNames.has(i.name) }));
      const generatedNames = new Set(generatedIntents.map((i) => i.name));
      const newActionTypes = new Set(generatedIntents.flatMap((i) => (i.actions || []).map((a) => a.type)));

      // Auto-apply immediately — replace any existing intent sharing a name, append the rest.
      setForm((prev) => ({
        ...prev,
        intents: [...prev.intents.filter((i) => !generatedNames.has(i.name)), ...generatedIntents],
        enabledActions: [...new Set([...(prev.enabledActions || []), ...newActionTypes])],
      }));

      setApplied(withReplaceFlag);
      setUsage(data.usage);
    } catch {
      toast.error("Failed to generate menu. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Own heading/Paper chrome dropped (Rahim, 2026-09-03: "add each section
  // into its own card just like we did in config") — StepActions now wraps
  // this in the same SectionCard every other Actions/Config section uses,
  // heading+description included, so this component is content-only from
  // here down.
  return (
    <Box sx={{ mt: 1.5 }}>
      <Button
        variant="contained" size="small" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
        onClick={generate} disabled={loading || !hasEnoughInput}
      >
        {loading ? "Generating…" : "Generate menu with AI"}
      </Button>
      {!hasEnoughInput && (
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", display: "block", mt: 0.75 }}>
          Fill in Context, Conversation Flow or Business Rules first.
        </Typography>
      )}
      {catalogSendEnabled && catalogsStatus !== "loading" && (!catalogs || catalogs.length === 0) && (
        <Typography sx={{ fontSize: "0.75rem", color: "warning.main", display: "block", mt: 0.75 }}>
          No catalogs found for this workspace yet — catalog_send actions can't be generated
          until at least one product catalog is connected.
        </Typography>
      )}

      {applied && applied.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Alert severity="success" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
            Added {applied.length} menu item{applied.length === 1 ? "" : "s"} to this agent — also visible under
            Intents on the Config step. Remember to Save Agent to persist them.
          </Alert>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {applied.map((intent) => (
              <Box key={intent.name}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>{intent.name.replace(/_/g, " ")}</Typography>
                  {intent.isReplace && <Chip label="Replaced existing" size="small" color="warning" variant="outlined" />}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, my: 0.5 }}>
                  {intent.keywords.map((k) => (
                    <Chip key={k} label={k} size="small" variant="outlined" />
                  ))}
                </Box>
                {intent.actions?.[0] && (
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    {describeAction(intent.actions[0])}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          {usage && (
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", display: "block", mt: 1 }}>
              Generation used ~{(usage.promptTokens || 0) + (usage.completionTokens || 0)} tokens.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
